/**
 * Recovery of a rejected Odoo `queue.message` request body.
 *
 * When Odoo refuses an inbound `POST /kw_api/custom/queue.message` during validation, no
 * `queue.message` record is created — but Odoo still logs the request in `kw.api.log`. That
 * rejected request body is exactly the body an operator needs in order to correct the payload
 * and resubmit it, so the Odoo Payment Update page can work for orders that have no
 * `queue.message` at all.
 *
 * Pure decision logic only, so it stays unit-testable; the components own presentation.
 *
 * Spec: docs/superpowers/specs/2026-08-11-odoo-payment-update-recover-rejected-body-design.md
 */

import type { QueueMessage } from './use-queue-message-poll';

export type QueueMessageBodyExtractionReason =
  'no-request-body' | 'malformed-json' | 'origin-mismatch';

/** Payload of `GET /odoo/sale-order/rejected-queue-message-body/:orderUuid`. */
export interface RecoveredRejection {
  recoverable: boolean;
  body: Record<string, unknown> | null;
  operationType: string | null;
  sourceLogId: number | null;
  attemptedAt: string | null;
  httpCode: string | null;
  validationErrors: string[] | null;
  errorMessage: string | null;
  /**
   * Whether Odoo logged a rejected request at all. A flag rather than a count: the backend asks
   * Odoo for the single newest rejection, so a total would be a number nobody measured.
   */
  hasRejectedAttempt: boolean;
  unrecoverableReason: QueueMessageBodyExtractionReason | null;
}

export type OdooPaymentLookupOutcome =
  /** Odoo holds a queue.message — the original flow. */
  | { kind: 'existing'; queueMessage: QueueMessage }
  /** No queue.message, but a rejected request yielded a reusable body. */
  | { kind: 'recovered'; queueMessage: QueueMessage; recovery: RecoveredRejection }
  /** Odoo refused the request and the body cannot be reused. */
  | { kind: 'rejected'; recovery: RecoveredRejection }
  /** Odoo has no record of any attempt — asking it to prepare an invoice is the right move. */
  | { kind: 'no-attempt' };

/**
 * Decides which of the four Odoo states the page is in.
 *
 * `recovery` is null when the recovery lookup itself failed; that must degrade to `no-attempt`
 * rather than dead-ending the operator, so a transient Odoo problem still leaves the trigger
 * path available.
 */
export function resolveLookupOutcome(
  queueMessage: QueueMessage | null | undefined,
  recovery: RecoveredRejection | null | undefined,
): OdooPaymentLookupOutcome {
  if (queueMessage?.id) {
    return { kind: 'existing', queueMessage };
  }
  if (!recovery) {
    return { kind: 'no-attempt' };
  }
  if (recovery.recoverable && recovery.body) {
    return {
      kind: 'recovered',
      queueMessage: buildRecoveredQueueMessage(recovery),
      recovery,
    };
  }
  if (recovery.hasRejectedAttempt) {
    return { kind: 'rejected', recovery };
  }
  return { kind: 'no-attempt' };
}

/**
 * Shapes a recovered body as a submittable queue.message. `id` stays null: nothing exists in
 * Odoo yet, and the submit path creates a new record rather than updating one.
 */
export function buildRecoveredQueueMessage(recovery: RecoveredRejection): QueueMessage {
  return {
    id: null,
    model: 'sale.order',
    format: true,
    body: recovery.body ?? {},
  };
}

/**
 * Ownership rule for the two editing surfaces: the payment form owns `body.payment`, the raw
 * JSON editor owns every other key. Rewriting only `payment` is what keeps an operator's
 * non-payment edits from being silently discarded when they touch a payment field.
 */
export function mergePaymentIntoBody(
  baseBody: Record<string, unknown>,
  payment: unknown,
): Record<string, unknown> {
  return { ...baseBody, payment };
}

/** Validates an operator-edited JSON body before it can be submitted to Odoo. */
export function parseBodyDraft(text: string): {
  body: Record<string, unknown> | null;
  error: string | null;
} {
  if (!text?.trim()) {
    return { body: null, error: 'The body is empty — reset it to the recovered version.' };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { body: null, error: 'This is not valid JSON — check for a missing comma or brace.' };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { body: null, error: 'The body must be a JSON object.' };
  }
  return { body: parsed as Record<string, unknown>, error: null };
}

/**
 * Odoo returns timestamps as `YYYY-MM-DD HH:mm:ss` in UTC, with no zone marker. Passing that
 * straight to `new Date()` makes engines read it as browser-local time, which would shift a
 * fresh rejection into the past and stop the poll from noticing it.
 */
export function parseOdooDateTime(value: string | null | undefined): Date | null {
  if (!value?.trim()) return null;
  const normalised = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value.trim())
    ? `${value.trim().replace(' ', 'T')}Z`
    : value.trim();
  const parsed = new Date(normalised);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Whether a logged rejection is newer than the one already seen at lookup, and therefore belongs
 * to the attempt we just triggered.
 *
 * Compares `kw.api.log` ids rather than timestamps: Odoo ids are monotonic, so this needs no
 * agreement between the browser clock and Odoo's — the same reasoning behind
 * `ResultCard.isFreshlyCreated`. A pre-existing rejection must not abort a poll that is still
 * legitimately waiting, and missing data errs towards continuing to wait.
 */
export function isAttemptNewerThanLogId(
  sourceLogId: number | null | undefined,
  seenLogId: number | null | undefined,
): boolean {
  if (sourceLogId == null) return false;
  if (seenLogId == null) return true;
  return sourceLogId > seenLogId;
}
